import React from "react";
import { Link } from "react-router-dom";
import { PublicDocShell } from "../components/PublicDocShell";
import { detectBrowser } from "../utils/browserDetection";
import "./HelpCenterPage.css";

export const HelpCenterPage: React.FC = () => {
  return (
    <PublicDocShell
      eyebrow="Support & Help"
      title={
        <>
          Help <span className="public-doc-title-gradient">Center</span>
        </>
      }
      subtitle="Get the support you need to maximize your job search success with Kairos's extension and platform."
    >
      <div className="help-content">
        {/* Getting Started */}
        <div className="help-section">
          <h2 className="help-section-title">
            <svg
              className="help-section-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
            Getting Started with Kairos
          </h2>
          <div className="help-section-content">
            <h3 className="help-subsection-title">1. Install the Extension</h3>
            <p>
              <strong>Critical Requirement:</strong> The Kairos browser
              extension is essential for accessing the full functionality of our
              platform. Without the extension, you will be limited to manual
              data entry, which is time-consuming, error-prone, and
              significantly reduces the effectiveness of our AI-powered job
              search and application optimization features.
            </p>
            <p>
              <strong>Why Installation is Required:</strong> The extension
              enables real-time job analysis directly from job boards, automatic
              application tracking, instant resume matching, and seamless
              integration with major platforms like LinkedIn, Indeed, and
              Glassdoor. It transforms your job search experience by providing
              instant insights, personalized recommendations, and one-click
              application capabilities that are simply not available through
              manual processes.
            </p>
            {(() => {
              const browserInfo = detectBrowser();
              const downloadUrl = browserInfo.extensionUrl;

              if (
                !downloadUrl ||
                browserInfo.type === "unknown" ||
                browserInfo.type === "safari"
              ) {
                return (
                  <p>
                    <strong>Browser Support:</strong> The Kairos extension is
                    available for Chrome, Microsoft Edge, Firefox, Opera, and
                    Brave browsers. If you are using a different browser, please
                    switch to a supported browser to install the extension.
                  </p>
                );
              }

              return (
                <div className="public-doc-install-callout">
                  <p className="public-doc-install-callout-title">
                    Install the Extension for {browserInfo.name}:
                  </p>
                  <a
                    href={downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="public-doc-install-button"
                  >
                    Install Extension for {browserInfo.name} →
                  </a>
                </div>
              );
            })()}
            <p>
              <strong>Next Steps:</strong> After installing the extension,
              create your Kairos account and complete your profile setup. The
              extension will automatically activate when you visit supported job
              boards, providing instant analysis and optimization features.
            </p>

            <h3 className="help-subsection-title">
              2. Understanding How It Works
            </h3>
            <p>
              <strong>Important:</strong> The Kairos extension only works when
              you're viewing individual job postings on supported job boards. It
              does not function on job listing pages or search results.
            </p>
            <p>
              <strong>Supported Platforms:</strong> LinkedIn, Indeed, Glassdoor,
              Monster, CareerBuilder, and other major job boards
            </p>
            <p>
              <strong>Activation:</strong> Navigate to a specific job posting
              page, and the Kairos extension will automatically appear
            </p>
          </div>
        </div>

        {/* How to Use the Extension */}
        <div className="help-section">
          <h2 className="help-section-title">
            <svg
              className="help-section-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
              />
            </svg>
            How to Use the Kairos Extension
          </h2>
          <div className="help-section-content">
            <h3 className="help-subsection-title">Step-by-Step Process</h3>
            <ol className="help-numbered-list">
              <li>
                <strong>Navigate to a Job Posting:</strong> Go to any supported
                job board and click on a specific job posting (not the job
                listing page)
              </li>
              <li>
                <strong>Extension Activation:</strong> The Kairos extension will
                automatically detect the job and display the analysis panel
              </li>
              <li>
                <strong>Job Analysis:</strong> Our system analyzes the job
                requirements, company culture, and your profile compatibility
              </li>
              <li>
                <strong>Personalized Insights:</strong> Receive tailored
                recommendations for resume optimization, cover letter
                suggestions, and interview preparation
              </li>
              <li>
                <strong>One-Click Application:</strong> Use the optimized
                materials to apply directly through the job board or company
                website
              </li>
            </ol>

            <h3 className="help-subsection-title">Key Features Available</h3>
            <ul className="help-bullet-list">
              <li>
                <strong>Resume Optimization:</strong> Suggestions to match your
                resume to the specific job requirements
              </li>
              <li>
                <strong>Cover Letter Generation:</strong> Personalized cover
                letter templates based on the job and company
              </li>
              <li>
                <strong>Skills Gap Analysis:</strong> Identify missing skills
                and get recommendations for improvement
              </li>
              <li>
                <strong>Company Culture Insights:</strong> Understand the
                company's values and work environment
              </li>
              <li>
                <strong>Interview Preparation:</strong> Tailored questions and
                answers based on the job description
              </li>
            </ul>
          </div>
        </div>

        {/* Troubleshooting */}
        <div className="help-section">
          <h2 className="help-section-title">
            <svg
              className="help-section-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
            Troubleshooting Common Issues
          </h2>
          <div className="help-section-content">
            <h3 className="help-subsection-title">Extension Not Appearing?</h3>
            <div className="help-warning-box">
              <p className="help-warning-title">Most Common Cause:</p>
              <p>
                <strong>You're not on an individual job posting page.</strong>{" "}
                The extension only works when viewing specific job details, not
                on job search results or company pages.
              </p>
            </div>

            <h3 className="help-subsection-title">Troubleshooting Steps</h3>
            <ol className="help-numbered-list">
              <li>
                <strong>Verify Page Type:</strong> Ensure you're on a specific
                job posting page (URL should contain job details, not search
                results)
              </li>
              <li>
                <strong>Check Extension Status:</strong> Click the Kairos
                extension icon in your browser toolbar to verify it's enabled
              </li>
              <li>
                <strong>Refresh the Page:</strong> Sometimes the extension needs
                a page refresh to activate properly
              </li>
              <li>
                <strong>Check Browser Compatibility:</strong> Ensure you're
                using a supported browser version
              </li>
              <li>
                <strong>Reinstall Extension:</strong> If issues persist, try
                reinstalling the extension
              </li>
            </ol>

            <h3 className="help-subsection-title">Supported Job Boards</h3>
            <div className="help-platforms-grid">
              <div className="help-platform-card">
                <p className="help-platform-title">Primary Platforms</p>
                <ul className="help-platform-list">
                  <li>LinkedIn Jobs</li>
                  <li>Indeed</li>
                  <li>Glassdoor</li>
                  <li>Monster</li>
                </ul>
              </div>
              <div className="help-platform-card">
                <p className="help-platform-title">Additional Platforms</p>
                <ul className="help-platform-list">
                  <li>CareerBuilder</li>
                  <li>ZipRecruiter</li>
                  <li>SimplyHired</li>
                  <li>Company Career Pages</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Account Management */}
        <div className="help-section">
          <h2 className="help-section-title">
            <svg
              className="help-section-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
            Account Management & Settings
          </h2>
          <div className="help-section-content">
            <h3 className="help-subsection-title">Profile Setup</h3>
            <p>
              <strong>Complete Your Profile:</strong> The more information you
              provide, the better our system can match you with opportunities
            </p>
            <ul className="help-bullet-list">
              <li>Upload your current resume</li>
              <li>Add your skills and certifications</li>
              <li>Specify your career goals and preferences</li>
              <li>Set your desired salary range and location preferences</li>
            </ul>

            <h3 className="help-subsection-title">Privacy & Data</h3>
            <p>
              <strong>Data Security:</strong> Your personal information is
              encrypted and never shared with employers without your consent
            </p>
            <p>
              <strong>Control Your Data:</strong> You can update, delete, or
              export your data at any time through your account settings
            </p>

            <h3 className="help-subsection-title">Notification Settings</h3>
            <p>
              <strong>Stay Informed:</strong> Configure how and when you receive
              notifications about job matches and application updates
            </p>
          </div>
        </div>

        {/* Best Practices */}
        <div className="help-section">
          <h2 className="help-section-title">
            <svg
              className="help-section-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
              />
            </svg>
            Best Practices for Success
          </h2>
          <div className="help-section-content">
            <h3 className="help-subsection-title">Maximizing Your Results</h3>
            <ul className="help-bullet-list">
              <li>
                <strong>Keep Your Profile Updated:</strong> Regularly update
                your skills, experience, and preferences
              </li>
              <li>
                <strong>Use the Extension Consistently:</strong> Apply to jobs
                through the extension to build your profile
              </li>
              <li>
                <strong>Review Suggestions:</strong> Always review and customize
                generated content before submitting
              </li>
              <li>
                <strong>Track Your Applications:</strong> Monitor your
                application status and follow up appropriately
              </li>
              <li>
                <strong>Provide Feedback:</strong> Rate the quality of
                suggestions to help improve the system
              </li>
            </ul>

            <h3 className="help-subsection-title">Common Mistakes to Avoid</h3>
            <div className="help-error-box">
              <ul className="help-bullet-list">
                <li>Applying to jobs without reviewing suggestions</li>
                <li>Not updating your profile with new skills or experience</li>
                <li>Ignoring the extension when it's available on job pages</li>
                <li>Submitting generic applications without personalization</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Contact Support */}
        <div className="help-section help-section-highlight">
          <h2 className="help-section-title">
            <svg
              className="help-section-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            Still Need Help?
          </h2>
          <div className="help-section-content">
            <p>
              <strong>Technical Support:</strong> For extension installation
              issues, browser compatibility problems, or technical glitches
            </p>
            <p>
              <strong>Account Support:</strong> For login issues, profile
              problems, or account management questions
            </p>
            <p>
              <strong>Feature Support:</strong> For questions about how to use
              specific features or optimize your experience
            </p>

            <div className="help-contact-box">
              <p className="help-contact-title">Contact Information:</p>
              <p>
                <strong>Email:</strong> support@cognixisai.com
              </p>
              <p>
                <strong>Response Time:</strong> Within 24 hours during business
                days
              </p>
              <p>
                <strong>Priority Support:</strong> Available for premium users
              </p>
            </div>
          </div>
        </div>

        {/* Legal & Policies */}
        <div className="help-section">
          <h2 className="help-section-title">
            <svg
              className="help-section-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
              />
            </svg>
            Legal & Policies
          </h2>
          <div className="help-section-content">
            <p>
              For information about our terms of service, privacy practices, and
              legal policies, please review:
            </p>
            <ul className="help-bullet-list">
              <li>
                <Link to="/terms-of-service">Terms of Service</Link>
              </li>
              <li>
                <Link to="/cookie-policy">Cookie Policy</Link>
              </li>
              <li>
                <Link to="/privacy-policy">Privacy Policy</Link>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </PublicDocShell>
  );
};
